/**
 * AdminPayments — Configuration des passerelles de paiement (V2.4)
 * Utilise: /api/v1/admin/payment-config (GET, PATCH)
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CreditCard, Zap, Loader2, Save, Shield, TestTube, ToggleLeft, ToggleRight, Eye, EyeOff,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { api, formatApiError } from "../services/api";

const GATEWAYS = [
  { key: "paystack",     name: "Paystack",       desc: "Carte bancaire panafricaine", color: "#00C3F7" },
  { key: "orange_money", name: "Orange Money",   desc: "Mobile Money — Afrique de l'Ouest & Centrale", color: "#FF6600" },
];

export default function AdminPayments() {
  const [config, setConfig] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [showSecret, setShowSecret] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/admin/payment-config");
      setConfig(data.config);
      const d = {};
      for (const gw of GATEWAYS) {
        d[gw.key] = {
          enabled: data.config[gw.key]?.enabled || false,
          public_key: data.config[gw.key]?.public_key || "",
          secret_key: "",     // masqué : ne jamais préremplir
        };
      }
      setDrafts(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateDraft = (gw, field, value) => {
    setDrafts((p) => ({ ...p, [gw]: { ...(p[gw] || {}), [field]: value } }));
  };

  const setMode = async (mode) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/v1/admin/payment-config", { mode });
      setConfig(data.config);
      toast.success(`Mode ${mode === "production" ? "Production" : "Test"} activé`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveGateway = async (key) => {
    setSaving(true);
    try {
      const draft = drafts[key] || {};
      const patch = {
        [key]: {
          enabled: draft.enabled,
          public_key: draft.public_key || "",
        },
      };
      // Envoyer secret UNIQUEMENT si l'utilisateur en a saisi un nouveau
      if (draft.secret_key && draft.secret_key.trim()) {
        patch[key].secret_key = draft.secret_key.trim();
      }
      const { data } = await api.patch("/v1/admin/payment-config", patch);
      setConfig(data.config);
      updateDraft(key, "secret_key", "");   // clear input after save
      toast.success(`${GATEWAYS.find((g) => g.key === key).name} — Configuration sauvegardée`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PrivateLayout title="Configuration Paiements">
        <div className="afr-card" style={{ padding: "3rem", display: "grid", placeItems: "center" }}>
          <Loader2 size={22} className="afr-spin" style={{ color: "var(--afr-primary)" }} />
        </div>
      </PrivateLayout>
    );
  }

  const mode = config?.mode || "test";

  return (
    <PrivateLayout
      title="Configuration Paiements"
      breadcrumbs={[{ label: "Administration" }, { label: "Paiements" }]}
    >
      {/* Mode toggle */}
      <div className="afr-card" data-testid="payments-mode-card" style={{ padding: "1.2rem", marginBottom: "1.2rem", background: mode === "production" ? "linear-gradient(135deg, rgba(0,150,64,0.08), var(--afr-surface))" : "linear-gradient(135deg, rgba(244,180,0,0.08), var(--afr-surface))" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: mode === "production" ? "var(--afr-primary)" : "var(--afr-warning)", color: "#fff", display: "grid", placeItems: "center" }}>
            {mode === "production" ? <Shield size={22} /> : <TestTube size={22} />}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)" }}>
              Mode {mode === "production" ? "Production" : "Test"}
            </h3>
            <p className="afr-muted" style={{ margin: 0, fontSize: "0.88rem" }}>
              {mode === "production"
                ? "Les paiements sont réels. Assurez-vous que les clés sont celles de votre compte de production."
                : "Environnement de test — aucun paiement réel n'est traité. Utilisez les clés Test des passerelles."}
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button type="button" onClick={() => setMode("test")} disabled={saving} className={`afr-btn afr-btn--sm ${mode === "test" ? "afr-btn--primary" : "afr-btn--outline"}`} data-testid="payments-mode-test">
              <TestTube size={14} /> Test
            </button>
            <button type="button" onClick={() => setMode("production")} disabled={saving} className={`afr-btn afr-btn--sm ${mode === "production" ? "afr-btn--primary" : "afr-btn--outline"}`} data-testid="payments-mode-production">
              <Shield size={14} /> Production
            </button>
          </div>
        </div>
      </div>

      {/* Gateways */}
      <div className="afr-grid" style={{ gridTemplateColumns: "1fr", gap: "1rem" }} data-testid="payments-gateways">
        {GATEWAYS.map((gw) => {
          const draft = drafts[gw.key] || {};
          const state = config?.[gw.key] || {};
          const secretSet = state.secret_key_set;
          return (
            <article key={gw.key} className="afr-card" data-testid={`payments-gw-${gw.key}`} style={{ padding: "1.2rem 1.3rem", borderLeft: `4px solid ${gw.color}` }}>
              <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `color-mix(in oklab, ${gw.color} 18%, var(--afr-surface))`, color: gw.color, display: "grid", placeItems: "center" }}>
                  <CreditCard size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                    <strong style={{ fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", fontSize: "1.05rem" }}>{gw.name}</strong>
                    <span style={{ padding: "0.15rem 0.5rem", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, background: draft.enabled ? "rgba(0,150,64,0.14)" : "rgba(107,116,128,0.14)", color: draft.enabled ? "#009640" : "#6b7480" }}>
                      {draft.enabled ? "Activée" : "Désactivée"}
                    </span>
                  </div>
                  <p className="afr-muted" style={{ margin: "0.25rem 0 0.8rem", fontSize: "0.86rem" }}>{gw.desc}</p>
                </div>
                <button type="button" onClick={() => updateDraft(gw.key, "enabled", !draft.enabled)} className="afr-btn afr-btn--outline afr-btn--sm" data-testid={`payments-toggle-${gw.key}`}>
                  {draft.enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  {draft.enabled ? "Désactiver" : "Activer"}
                </button>
              </div>

              <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "0.8rem", marginTop: "0.8rem" }}>
                <div>
                  <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
                    Clé publique {mode === "test" ? "(Test)" : "(Production)"}
                  </label>
                  <input
                    type="text"
                    value={draft.public_key || ""}
                    onChange={(e) => updateDraft(gw.key, "public_key", e.target.value)}
                    placeholder="pk_..."
                    data-testid={`payments-public-${gw.key}`}
                    style={{ width: "100%", padding: "0.55rem 0.7rem", borderRadius: 8, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.86rem", color: "var(--afr-secondary)" }}
                  />
                </div>
                <div>
                  <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem", display: "flex", justifyContent: "space-between" }}>
                    <span>Clé secrète {mode === "test" ? "(Test)" : "(Production)"} {secretSet && <span style={{ color: "var(--afr-primary)" }}>✓ définie</span>}</span>
                    <button type="button" onClick={() => setShowSecret((p) => ({ ...p, [gw.key]: !p[gw.key] }))} style={{ background: "none", border: "none", color: "var(--afr-primary)", cursor: "pointer", padding: 0 }} data-testid={`payments-secret-toggle-${gw.key}`}>
                      {showSecret[gw.key] ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                  </label>
                  <input
                    type={showSecret[gw.key] ? "text" : "password"}
                    value={draft.secret_key || ""}
                    onChange={(e) => updateDraft(gw.key, "secret_key", e.target.value)}
                    placeholder={secretSet ? "•••• (déjà définie — laisser vide pour conserver)" : "sk_..."}
                    autoComplete="new-password"
                    data-testid={`payments-secret-${gw.key}`}
                    style={{ width: "100%", padding: "0.55rem 0.7rem", borderRadius: 8, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.86rem", color: "var(--afr-secondary)" }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.9rem" }}>
                <button type="button" onClick={() => saveGateway(gw.key)} disabled={saving} className="afr-btn afr-btn--primary afr-btn--sm" data-testid={`payments-save-${gw.key}`}>
                  {saving ? <Loader2 size={14} className="afr-spin" /> : <Save size={14} />}
                  Sauvegarder {gw.name}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <p className="afr-muted" style={{ fontSize: "0.82rem", marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
        <Zap size={14} style={{ marginTop: 3, color: "var(--afr-warning)" }} />
        Les clés secrètes sont chiffrées au repos et jamais renvoyées en clair par l'API. Seuls les administrateurs peuvent modifier cette configuration.
      </p>
    </PrivateLayout>
  );
}
